import {
  Box,
  Chip,
  Stack,
  alpha,
  Drawer,
  Toolbar,
  Divider,
  useTheme,
  Typography,
  IconButton,
  LinearProgress,
  linearProgressClasses,
} from '@mui/material';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { Book } from 'pages/dashboard';
import { renderFormattedDate } from 'utils';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import { PROJECT_STEP_PRIORITY_TYPE_ENUM } from 'enums';
import { Project, User, ProjectReportItem } from 'library/types';

export interface ProcessedProjectReportItem extends ProjectReportItem {
  projectName: string;
  productName: string;
  notes: string;
  relatedContactId: number | null;
  alternativeRelatedContactId: number | null;
  status: number;
  priority: number | null;
  startDate: Date | null;
  endDate: Date | null;
  progress: number;
}

type DetailItem = { label: string; value: ReactNode };

type DetailDrawerProps = {
  users: User[];
  books: Book[];
  products: Project[];
  open: boolean;
  close: () => void;
  detailRow: ProcessedProjectReportItem | null;
  getProjectStepStatusTypeTranslated: (status: number) => string;
  getPriorityTypeTranslated: (priority: number) => string;
};

const DetailDrawer = ({
  users,
  books,
  products,
  open,
  close,
  detailRow,
  getProjectStepStatusTypeTranslated,
  getPriorityTypeTranslated,
}: DetailDrawerProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const renderStatusChip = (status?: number) => {
    if (status == null) return '-';
    // Mapping: 1 -> Not Started (Blue), 2 -> In Progress (Secondary), 3 -> Completed (Success)
    const colorKey = status === 1 ? 'info' : status === 3 ? 'success' : 'secondary';

    return (
      <Chip
        size="small"
        label={getProjectStepStatusTypeTranslated(status)}
        sx={{
          fontWeight: 600,
          bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
          color: theme.palette[colorKey].main,
          border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
          cursor: 'default',
        }}
      />
    );
  };

  const renderPriorityChip = (priority?: number | null) => {
    if (priority == null) return '-';
    const colorKey =
      priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT
        ? 'success'
        : priority === PROJECT_STEP_PRIORITY_TYPE_ENUM.URGENT
          ? 'warning'
          : 'error';

    return (
      <Chip
        size="small"
        label={getPriorityTypeTranslated(priority)}
        sx={{
          fontWeight: 600,
          bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
          color: theme.palette[colorKey].main,
          border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
          cursor: 'default',
        }}
      />
    );
  };

  const renderRowDetails = (row: ProcessedProjectReportItem | null): DetailItem[] => {
    if (!row) return [];

    return [
      {
        label: intl.formatMessage({ id: 'projectReport.book', defaultMessage: 'Proje' }),
        value: books.find((b) => b.id === row.bookId)?.companyName ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'projectReport.product', defaultMessage: 'Ürün' }),
        value: products.find((p) => p.id === row.projectId)?.title ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'projectReport.notes', defaultMessage: 'Notlar' }),
        value: row.notes ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'projectReport.status', defaultMessage: 'Statü' }),
        value: renderStatusChip(row.status),
      },
      {
        label: intl.formatMessage({ id: 'projectReport.priority', defaultMessage: 'Öncelik' }),
        value: renderPriorityChip(row.priority),
      },
      {
        label: intl.formatMessage({ id: 'projectReport.progress', defaultMessage: 'İlerleme' }),
        value: (() => {
          const value = row.progress ?? 0;
          const progressColor = theme.palette.success.main;

          return (
            <Box sx={{ display: 'flex', width: '30%', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  [`&.${linearProgressClasses.colorPrimary}`]: {
                    bgcolor: alpha(progressColor, 0.15),
                  },
                  [`& .${linearProgressClasses.bar}`]: {
                    borderRadius: 4,
                    bgcolor: progressColor,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ minWidth: 38, fontWeight: 700, color: progressColor, textAlign: 'right' }}>
                {value}%
              </Typography>
            </Box>
          );
        })(),
      },
      {
        label: intl.formatMessage({ id: 'projectReport.relatedContact', defaultMessage: 'İlgili Kişi' }),
        value: users.find((u) => u.id === row.relatedContactId)?.nameSurname ?? '-',
      },
      {
        label: intl.formatMessage({
          id: 'projectReport.alternativeRelatedContact',
          defaultMessage: 'Yedek İlgili Kişi',
        }),
        value: users.find((u) => u.id === row.alternativeRelatedContactId)?.nameSurname ?? '-',
      },
      {
        label: intl.formatMessage({ id: 'projectReport.startDate', defaultMessage: 'Başlangıç Tarihi' }),
        value: row.startDate ? renderFormattedDate(dayjs(row.startDate)) : '-',
      },
      {
        label: intl.formatMessage({ id: 'projectReport.endDate', defaultMessage: 'Bitiş Tarihi' }),
        value: row.endDate ? renderFormattedDate(dayjs(row.endDate)) : '-',
      },
      {
        label: intl.formatMessage({ id: 'projectReport.updatedAt', defaultMessage: 'Son Güncellenme Tarihi' }),
        value: row.updatedAt ? renderFormattedDate(dayjs(row.updatedAt), true) : '-',
      },
      {
        label: intl.formatMessage({ id: 'projectReport.comments', defaultMessage: 'Yorumlar' }),
        value:
          !row.comments || row.comments.length === 0 ? (
            <Typography variant="caption" color="text.disabled">
              <FormattedMessage id="projectStepManaging.noCommentYet" defaultMessage="Henüz yorum eklenmemiş" />
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {row.comments.map((item) => (
                <Box key={item.id}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {renderFormattedDate(item.createdAt, true)} • {users.find((u) => u.id === item.userId)?.nameSurname}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
                    {item.commentText}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ),
      },
    ];
  };

  return (
    <Drawer anchor="right" open={open} onClose={close}>
      <Toolbar />
      <Box sx={{ width: { xs: '92vw', sm: 520 }, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">
            <FormattedMessage id="common.detail" defaultMessage="Detay" />
          </Typography>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
          {[
            books.find((b) => b.id === detailRow?.bookId)?.companyName,
            products.find((p) => p.id === detailRow?.projectId)?.title,
          ]
            .filter(Boolean)
            .join(' / ')}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1.25}>
          {renderRowDetails(detailRow).map((item) => (
            <Box key={item.label}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {item.label}
              </Typography>
              <Typography component="div" variant="body2" sx={{ wordBreak: 'break-word' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default DetailDrawer;
